import { useCallback, useEffect, useMemo } from "react";
import { usePatientLocalStorage } from "./use-patient-local-storage";

const EXPIRY_DAYS = 30;
const EXPIRY_DAYS_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

type ViewedRowsData = Record<string, number>; // composite key -> timestamp

export type UseRowViewState = {
  isRowViewed: (tableType: string, rowId: string) => boolean;
  markRowAsViewed: (tableType: string, rowId: string) => void;
  clearAllViews: () => void;
};

/**
 * Builds composite key for row identification across all tables.
 *
 * Format: `${tableType}:${rowId}` ensures row IDs are unique even if
 * the same ID exists in multiple table types.
 */
function buildCompositeKey(tableType: string, rowId: string): string {
  return `${tableType}:${rowId}`;
}

/**
 * Manages viewed/unviewed state for patient table rows.
 *
 * Tracks which rows a user has viewed (opened in side panel) with Gmail-style
 * behavior. View state is persisted to localStorage per-patient and expires
 * after 30 days. Automatically cleans up expired entries on mount.
 *
 * @param patientId - Patient UUID to scope the view state
 * @returns Methods to check and update row view state
 *
 * @example
 * ```typescript
 * const { isRowViewed, markRowAsViewed } = useRowViewState(patientId);
 *
 * // Check if row was viewed
 * const viewed = isRowViewed('DiagnosticReport', 'abc-123');
 *
 * // Mark as viewed (typically when side panel loads)
 * markRowAsViewed('DiagnosticReport', 'abc-123');
 * ```
 */
export function useRowViewState(patientId: string): UseRowViewState {
  const { data, updateData, setData } = usePatientLocalStorage<ViewedRowsData>(
    patientId,
    "viewedRows",
    {}
  );

  // Convert to Map for O(1) lookups
  const viewedRowsMap = useMemo(() => new Map(Object.entries(data)), [data]);

  function clearExpiredViews() {
    const now = Date.now();
    const filtered = Object.entries(data).filter(
      ([, timestamp]) => now - timestamp < EXPIRY_DAYS_MS
    );
    setData(Object.fromEntries(filtered));
  }

  /**
   * Clear expired views on mount.
   *
   * Design choice: Only runs when patientId changes, NOT when data changes.
   * This is a deliberate performance optimization with acceptable trade-offs:
   *
   * - Runs once per patient load (efficient)
   * - Avoids running expiry checks on every row click (performance)
   * - Trade-off: Entries that expire during a session won't be cleaned until next mount
   * - Acceptable because: Users are unlikely to have 30-day browser sessions
   *
   * Alternative considered: Include `data` in deps → would run on every click → poor performance
   */
  useEffect(() => {
    clearExpiredViews();
  }, [patientId]); // Only run when patient changes

  const isRowViewed = useCallback(
    (tableType: string, rowId: string) => {
      const key = buildCompositeKey(tableType, rowId);
      return viewedRowsMap.has(key);
    },
    [viewedRowsMap]
  );

  const markRowAsViewed = useCallback(
    (tableType: string, rowId: string) => {
      const key = buildCompositeKey(tableType, rowId);
      updateData(prev => ({
        ...prev,
        [key]: Date.now(),
      }));
    },
    [updateData]
  );

  const clearAllViews = useCallback(() => {
    setData({});
  }, [setData]);

  return {
    isRowViewed,
    markRowAsViewed,
    clearAllViews,
  };
}
